import React, { useEffect, useMemo, useState, lazy } from 'react';
import { X, Check, Loader2, Flame, Sparkles } from 'lucide-react';
import { optimizeImg } from '../utils/img';
import { warmCheckoutConnection } from '../utils/warmCheckout';
export interface UpsellProduct {
  id: string;
  name: string;
  variantId: string;
  normalPrice: number;
  offerPrice: number;
  benefit: string;
  image: string;
}
interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  mainPrice: number;
  upsellProducts: UpsellProduct[];
  bundlePrice: number;
  bundleComparePrice: number;
  buildCheckoutUrl: (selectedVariantIds: string[], isBundle?: boolean) => string;
}
// Mexican number format: 1299 -> "1,299"
const formatPrice = (n: number) => `${n.toLocaleString('es-MX')}`;
export function UpsellModal({
  isOpen,
  onClose,
  mainPrice,
  upsellProducts,
  bundlePrice,
  bundleComparePrice,
  buildCheckoutUrl
}: UpsellModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bundleActive, setBundleActive] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'primary' | 'secondary' | null>(null);
  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      // Strongest purchase-intent signal — warm the checkout connection now.
      warmCheckoutConnection();
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);
  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loadingAction) {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, loadingAction, onClose]);
  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setLoadingAction(null);
      setSelectedIds(new Set());
      setBundleActive(false);
    }
  }, [isOpen]);
  const toggleProduct = (id: string) => {
    if (loadingAction) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // Any individual toggle deactivates the bundle pricing
      setBundleActive(false);
      return next;
    });
  };
  const activateBundle = () => {
    if (loadingAction) return;
    // Select all 3 products and apply bundle pricing
    setSelectedIds(new Set(upsellProducts.map(p => p.id)));
    setBundleActive(true);
  };
  const selectedProducts = useMemo(() => upsellProducts.filter(p => selectedIds.has(p.id)), [selectedIds, upsellProducts]);
  const individualUpsellTotal = useMemo(() => selectedProducts.reduce((sum, p) => sum + p.offerPrice, 0), [selectedProducts]);
  // If all 3 are selected, auto-promote to bundle pricing in the UI for clarity
  const allThreeSelected = selectedIds.size === upsellProducts.length;
  const effectiveBundle = bundleActive || allThreeSelected;
  const upsellTotal = effectiveBundle ? bundlePrice : individualUpsellTotal;
  const grandTotal = mainPrice + upsellTotal;
  // "Antes" price for the bundle comparison (configurable in App.tsx)
  const bundleNormalSum = bundleComparePrice;
  const bundleSavings = bundleNormalSum - bundlePrice;
  const handlePrimary = () => {
    if (loadingAction) return;
    setLoadingAction('primary');
    const url = buildCheckoutUrl(selectedProducts.map(p => p.variantId), effectiveBundle);
    window.location.href = url;
  };
  const handleSecondary = () => {
    if (loadingAction) return;
    setLoadingAction('secondary');
    const url = buildCheckoutUrl([]);
    window.location.href = url;
  };
  const handleClose = () => {
    if (loadingAction) return;
    onClose();
  };
  if (!isOpen) return null;
  const hasSelection = selectedProducts.length > 0;
  return <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-4 animate-[fadeIn_0.25s_ease-out]" role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-269">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink-900/70 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" data-id="element-270" />

      {/* Modal */}
      <div className="relative w-full md:max-w-2xl bg-cream-50 rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden animate-[slideUp_0.35s_cubic-bezier(0.22,1,0.36,1)] h-[96vh] md:h-auto md:max-h-[94vh] flex flex-col" style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-271">
        {/* Close button */}
        <button onClick={handleClose} disabled={!!loadingAction} className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-cream-50/95 hover:bg-cream-200 flex items-center justify-center text-ink-700 transition disabled:opacity-50 shadow-sm" aria-label="Cerrar" data-id="element-272">
          <X className="w-4 h-4" data-id="element-273" />
        </button>

        {/* Header */}
        <div className="bg-olive-700 text-cream-50 px-5 pt-5 pb-4 text-center" data-id="element-274">
          <div className="inline-flex items-center gap-1.5 bg-[#B8924A] text-cream-50 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider mb-3" data-id="element-275">
            <Flame className="w-3 h-3" data-id="element-276" />
            Oferta especial por tiempo limitado
          </div>
          <h2 id="upsell-title" className="font-serif text-lg md:text-2xl font-medium leading-tight mb-2 px-6" data-id="element-277">
            Completa tu biblioteca natural
          </h2>
          <p className="text-xs md:text-sm text-cream-200 leading-relaxed" data-id="element-278">
            Selecciona los ebooks que quieres sumar a tu Biblioteca Natural
            antes de finalizar tu compra.
          </p>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto px-4 md:px-5 py-4 flex-1 space-y-3" data-id="element-279">
          {/* BUNDLE DEAL banner */}
          <div className={`relative rounded-2xl p-4 border-2 transition-all overflow-hidden ${effectiveBundle ? 'border-[#B8924A] bg-gradient-to-br from-[#FBF5E8] to-[#F5EAD0] shadow-md' : 'border-[#B8924A]/50 bg-gradient-to-br from-[#FDF9EF] to-[#FAF3DE]'}`} data-id="element-280">
            {/* Best deal ribbon */}
            <div className="absolute top-0 right-0 bg-[#B8924A] text-cream-50 text-[9px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-bl-lg flex items-center gap-1" data-id="element-281">
              <Sparkles className="w-2.5 h-2.5" data-id="element-282" />
              Mejor oferta
            </div>

            <div className="flex items-start gap-3 mb-3" data-id="element-283">
              {/* Stacked thumbnails */}
              <div className="flex-shrink-0 flex -space-x-3" data-id="element-284">
                {upsellProducts.map((p, idx) => <div key={p.id} className="w-9 h-12 rounded-md overflow-hidden border-2 border-cream-50 shadow-sm" style={{
                zIndex: upsellProducts.length - idx
              }} data-id="element-285">
                    <img src={optimizeImg(p.image, {
                  w: 36,
                  h: 48,
                  q: 60
                })} alt="" width={36} height={48} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-286" />
                  </div>)}
              </div>

              <div className="flex-1 min-w-0 pr-14" data-id="element-287">
                <h3 className="font-serif font-semibold text-sm md:text-base text-ink-900 leading-tight mb-1" data-id="element-288">
                  Llévate los 3 ebooks juntos
                </h3>
                <p className="text-xs text-ink-700 leading-snug" data-id="element-289">
                  El pack completo a precio especial — ahorras{' '}
                  {formatPrice(bundleSavings)}.
                </p>
              </div>
            </div>

            {/* Bundle pricing */}
            <div className="flex items-baseline gap-2 mb-3" data-id="element-290">
              <span className="text-xs text-ink-500 line-through" data-id="element-291">
                {formatPrice(bundleNormalSum)}
              </span>
              <span className="font-serif text-2xl font-semibold text-[#B8924A]" data-id="element-292">
                {formatPrice(bundlePrice)}
              </span>
              <span className="text-[10px] font-semibold text-olive-700 bg-olive-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider" data-id="element-293">
                Pack 3 ebooks
              </span>
            </div>

            <button type="button" onClick={activateBundle} disabled={!!loadingAction || effectiveBundle} className={`w-full text-sm font-semibold py-2.5 rounded-full transition-all flex items-center justify-center gap-2 ${effectiveBundle ? 'bg-olive-600 text-cream-50 cursor-default' : 'bg-[#B8924A] hover:bg-[#A07F3D] active:scale-[0.99] text-cream-50 shadow-sm'}`} data-id="element-294">
              {effectiveBundle ? <>
                  <Check className="w-4 h-4" strokeWidth={3} data-id="element-295" />
                  <span data-id="element-296">Pack activado</span>
                </> : <span data-id="element-297">Llevar los 3 por {formatPrice(bundlePrice)}</span>}
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1" data-id="element-298">
            <div className="flex-1 h-px bg-cream-300" data-id="element-299"></div>
            <span className="text-[10px] text-ink-600 uppercase tracking-wider font-medium" data-id="element-300">
              o elige individuales
            </span>
            <div className="flex-1 h-px bg-cream-300" data-id="element-301"></div>
          </div>

          {/* Individual product cards */}
          {upsellProducts.map(product => {
          const isSelected = selectedIds.has(product.id);
          return <button key={product.id} type="button" onClick={() => toggleProduct(product.id)} disabled={!!loadingAction} className={`w-full text-left rounded-2xl border-2 transition-all p-3.5 flex items-start gap-3 disabled:opacity-50 ${isSelected ? 'border-olive-600 bg-olive-50 shadow-sm' : 'border-cream-300 bg-cream-50 hover:border-olive-300'}`} aria-pressed={isSelected} data-id="element-302">
                {/* Product cover */}
                <div className={`flex-shrink-0 w-16 h-20 md:w-20 md:h-24 rounded-lg overflow-hidden bg-cream-100 border transition-colors ${isSelected ? 'border-olive-600' : 'border-cream-300'}`} data-id="element-303">
                  <img src={optimizeImg(product.image, {
                w: 80,
                h: 96,
                q: 65
              })} alt={product.name} width={80} height={96} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-304" />
                </div>

                {/* Body */}
                <div className="flex-1 min-w-0" data-id="element-305">
                  <div className="flex items-start justify-between gap-2 mb-1" data-id="element-306">
                    <h3 className="font-serif font-medium text-sm md:text-base text-ink-900 leading-tight flex-1" data-id="element-307">
                      {product.name}
                    </h3>
                    <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-olive-600 border-olive-600' : 'bg-cream-50 border-ink-500/40'}`} data-id="element-308">
                      {isSelected && <Check className="w-3 h-3 text-cream-50" strokeWidth={3.5} data-id="element-309" />}
                    </div>
                  </div>

                  <p className="text-xs text-ink-700 leading-snug mb-2" data-id="element-310">
                    {product.benefit}
                  </p>

                  <div className="flex items-center gap-2 flex-wrap" data-id="element-311">
                    <span className="text-[10px] font-semibold text-[#B8924A] uppercase tracking-wider bg-[#B8924A]/10 px-2 py-0.5 rounded-full" data-id="element-312">
                      Oferta especial
                    </span>
                    <span className="text-xs text-ink-500 line-through" data-id="element-313">
                      {formatPrice(product.normalPrice)}
                    </span>
                    <span className="font-serif text-base font-semibold text-olive-700" data-id="element-314">
                      {formatPrice(product.offerPrice)}
                    </span>
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* Sticky bottom bar */}
        <div className="border-t border-cream-200 px-4 pt-3 pb-4 bg-cream-50 flex-shrink-0 space-y-2.5" data-id="element-315">
          {/* Live totals */}
          {hasSelection && <div className="bg-cream-100 rounded-xl px-3.5 py-2.5 text-xs space-y-1" data-id="element-316">
              <div className="flex items-center justify-between text-ink-700" data-id="element-317">
                <span data-id="element-318">
                  {effectiveBundle ? 'Pack 3 ebooks (oferta)' : 'Complementos seleccionados'}
                </span>
                <span className="font-semibold text-olive-700" data-id="element-319">
                  {formatPrice(upsellTotal)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-1.5 border-t border-cream-300" data-id="element-320">
                <span className="font-semibold text-ink-900 text-sm" data-id="element-321">
                  Total de tu compra
                </span>
                <span className="font-serif font-semibold text-ink-900 text-base" data-id="element-322">
                  {formatPrice(grandTotal)}
                </span>
              </div>
            </div>}

          {/* Primary button */}
          <button onClick={handlePrimary} onMouseEnter={warmCheckoutConnection} onTouchStart={warmCheckoutConnection} disabled={!!loadingAction} className="w-full bg-[#B8924A] hover:bg-[#A07F3D] active:scale-[0.99] text-cream-50 font-semibold text-sm md:text-base py-3.5 rounded-full transition-all flex items-center justify-center gap-2 disabled:opacity-70 shadow-md" data-id="element-323">
            {loadingAction === 'primary' ? <>
                <Loader2 className="w-4 h-4 animate-spin" data-id="element-324" />
                <span data-id="element-325">Procesando…</span>
              </> : hasSelection ? <span data-id="element-326">Agregar a mi pedido por {formatPrice(upsellTotal)}</span> : <span data-id="element-327">Continuar solo con mi compra</span>}
          </button>

          {/* Secondary "No gracias" — always visible */}
          <button onClick={handleSecondary} onMouseEnter={warmCheckoutConnection} onTouchStart={warmCheckoutConnection} disabled={!!loadingAction} className="w-full bg-cream-50 border-2 border-ink-500/30 text-ink-700 hover:bg-cream-100 hover:border-ink-500/50 active:scale-[0.99] text-sm md:text-base font-semibold py-3 rounded-full transition disabled:opacity-50 flex items-center justify-center gap-2" data-id="element-328">
            {loadingAction === 'secondary' ? <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" data-id="element-329" />
                <span data-id="element-330">Procesando…</span>
              </> : <span data-id="element-331">No gracias, continuar</span>}
          </button>

          {/* Small print */}
          <p className="text-[10px] text-ink-600 text-center leading-snug pt-0.5" data-id="element-332">
            Se agrega automáticamente a tu compra y recibes todo junto en
            formato digital.
          </p>
        </div>
      </div>

      <style data-id="element-333">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>;
}